import { FC, useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { validateEmail } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import Eye from '../../assets/icons/eye.svg?react';
import EyeSlash from '../../assets/icons/eyeSlash.svg?react';

export interface SocialButton {
    name: string;
    icon: string;
    label?: string;
    onClick: () => Promise<void>;
}

export const SignIn: FC = () => {
    const { signInUser, error } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState<{ [key: string]: { value: string, error: string } }>({ email: { value: '', error: '' }, password: { value: '', error: '' } });
    const { setCurrentScreen } = useAuth();
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: { ...prev[e.target.name], value: e.target.value, error: '' } }));
    }
    const validatePassword = (password: string): string => {
        if (!password.trim()) return 'Password is required';
        if (password.trim().length < 8) return 'Min 8 chars';
        return '';
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        // Validate all fields at once
        const emailError = validateEmail(form.email.value);
        const passwordError = validatePassword(form.password.value);
        
        // Update form with all errors
        setForm(prev => ({
            ...prev,
            email: { ...prev.email, error: emailError },
            password: { ...prev.password, error: passwordError }
        }));
        
        // Only proceed if all validations pass
        if (!emailError && !passwordError) {
            signInUser({ email: form.email.value, password: form.password.value.trim() });
        }
    };

    const handleBlurOnEmail = () => {
        setForm(prev => ({ ...prev, email: { ...prev.email, error: validateEmail(form.email.value) } }));
    }

    const handleBlurOnPassword = () => {
        setForm(prev => ({ ...prev, password: { ...prev.password, error: validatePassword(form.password.value) } }));
    }

    useEffect(() => {
        if (error?.includes('Invalid email or password')) {
            setForm(prev => ({ ...prev, email: { ...prev.email, error: 'Invalid email' }, password: { ...prev.password, error: 'Invalid password' } }));
        }
    }, [error]);

    return (
        <section className="flex flex-col gap-4 w-full">
            <h2 className="sm:text-2xl text-xl font-medium text-center text-white">Log In and Listen with RedFM</h2>
            <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
                <Input
                    variant="secondary"
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    name="email"
                    value={form.email.value}
                    maxLength={200}
                    onChange={handleChange}
                    onBlur={handleBlurOnEmail}
                    error={form.email.error}
                />
                <div className="flex flex-col items-end gap-1">
                    <Input
                        variant="secondary"
                        type={showPassword ? "text" : "password"}
                        label="Password"
                        placeholder="Enter your password"
                        name="password"
                        value={form.password.value}
                        onChange={handleChange}
                        maxLength={20}
                        error={form.password.error}
                        iconPosition="trailing"
                        icon={showPassword ? <Eye /> : <EyeSlash />}
                        onIconClick={() => setShowPassword(!showPassword)}
                        onBlur={handleBlurOnPassword}
                        className="w-full"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setCurrentScreen("forgotPassword");

                        }}
                        className="underline text-[#D0D5DD] focus:text-primary-700 outline-none text-xs sm:text-sm font-semibold"
                    >
                        Forgot password?
                    </button>
                </div>
                <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                >
                    Log In
                </Button>
            </form>
        </section>
    );
};
